import { axios } from '@/plugins/axios';
import { KeyValue, KeyValuePair, Pagination, Resource, Statement } from "@/helpers/Interfaces";
import Pager from "@/helpers/Pager";
import merge from "lodash/merge";
import { IMetaModel } from '@/modules/core/common/services/GridService';
import { UserDisplayModel } from '@/modules/core/dictionaries/services/DictionaryService';
import { DateTime } from 'luxon';

export default class ProcessService
{
    public static async getDiagram(processId: string, moduleVersionId?: string): Promise<Resource<DiagramDataModel>>
    {
        const headers = moduleVersionId ? { 'Moduleversion': moduleVersionId } : {};

        return (await axios.get(`simple-fetch/process/${processId}/flow`, {
            headers
        })).data;
    }

    public static async getActions(moduleId: string, dictionaryType: string): Promise<Record<string, IMetaModel>>
    {
        return (await axios.get(`modules/${moduleId}/process/settings/${dictionaryType}/actions`)).data;
    }

    public static async createNewVersion(id: string): Promise<string>
    {
        return (await axios.get(`process/${id}/new-version`)).data;
    }

    public static async getResourceActions(publicId: string): Promise<Record<string, IMetaModel>>
    {
        return (await axios.get(`simple-query/process/${publicId}/actions`)).data;
    }

    public static async getFormActions(id: string): Promise<Record<string, IMetaModel>>
    {
        return (await axios.get(`simple-query/process/${id}/actions`)).data;
    }

    public static async saveDiagram(processId: string, xml: string, features: FeaturesForm[]): Promise<any>
    {
        return (await axios.post(`process/flow/${processId}`, {
            xml: xml,
            features: features
        })).data;
    }

    public static async getProcessDetails(processId: string): Promise<Resource<DetailsModel>>
    {
        return (await axios.get(`simple-fetch/process/${processId}`)).data;
    }

    public static async addProcess(form: CreateModel): Promise<Resource<ProcessFormStatement>>
    {
        return (await axios.post('simple-command/process', form)).data;
    }

    public static async addModuleProcess(form: ModuleProcessModel, moduleId: string): Promise<string>
    {
        return (await axios.post(`simple-command/modules/${moduleId}/module-process`, form)).data;
    }

    public static async editProcess(processId: string, form: CreateModel | ModuleProcessModel): Promise<Statement>
    {
        return (await axios.put(`simple-command/process/${processId}`, form)).data;
    }

    public static async processList(pager: Pager, filter: any): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('simple-query/process', {
            params: merge({}, pager.data(), filter)
        })).data;
    }

    public static async flowControls(form: XmlInfoForm, infoId: string): Promise<ControlsResult>
    {
        return (await axios.post('process/flow/controls', {
            form: form,
            infoId: infoId
        })).data;
    }

    public static async getActionsList(processId: string, pager: Pager): Promise<Pagination<Resource<ActionDetailsModel>>>
    {
        return (await axios.get(`process/${processId}/settings/actions`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async saveActions(processId: string, form: ActionCreateModel): Promise<KeyValuePair>
    {
        return (await axios.post(`process/${processId}/settings/actions`, form)).data;
    }

    public static async editActions(processId: string, dictionaryId: number, form: ActionCreateModel): Promise<Statement>
    {
        return (await axios.post(`process/${processId}/settings/actions/${dictionaryId}`, form)).data;
    }

    public static async getModuleActionsList(moduleId: string, pager: Pager): Promise<Pagination<Resource<ActionDetailsModel>>>
    {
        return (await axios.get(`modules/${moduleId}/process/settings/actions`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async saveModuleActions(moduleId: string, form: ActionCreateModel): Promise<Statement>
    {
        return (await axios.post(`modules/${moduleId}/process/settings/actions`, form)).data;
    }

    public static async editModuleActions(moduleId: string, dictionaryId: number, form: ActionCreateModel): Promise<Statement>
    {
        return (await axios.post(`modules/${moduleId}/process/settings/actions/${dictionaryId}`, form)).data;
    }

    public static async getActionDetails(processId: string, id: number): Promise<ActionDetailsModel>
    {
        return (await axios.get(`process/${processId}/settings/actions/${id}`)).data;
    }

    public static async getMembersList(processId: string, pager: Pager): Promise<Pagination<Resource<MemberDetailsModel>>>
    {
        return (await axios.get(`process/${processId}/settings/members`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async saveMembers(processId: string, form: MemberCreateModel): Promise<Statement>
    {
        return (await axios.post(`process/${processId}/settings/members`, form)).data;
    }

    public static async editMembers(processId: string, dictionaryId: number, form: MemberCreateModel): Promise<Statement>
    {
        return (await axios.post(`process/${processId}/settings/members/${dictionaryId}`, form)).data;
    }

    public static async getModuleMembersList(moduleId: string, pager: Pager): Promise<Pagination<Resource<MemberDetailsModel>>>
    {
        return (await axios.get(`modules/${moduleId}/process/settings/members`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async saveModuleMembers(moduleId: string, form: MemberCreateModel): Promise<Statement>
    {
        return (await axios.post(`modules/${moduleId}/process/settings/members`, form)).data;
    }

    public static async editModuleMembers(moduleId: string, dictionaryId: number, form: MemberCreateModel): Promise<Statement>
    {
        return (await axios.post(`modules/${moduleId}/process/settings/members/${dictionaryId}`, form)).data;
    }

    public static async getMemberDetails(processId: string, id: number): Promise<MemberDetailsModel>
    {
        return (await axios.get(`process/${processId}/settings/members/${id}`)).data;
    }

    public static async getStatusList(processId: string, pager: Pager): Promise<Pagination<Resource<StatusDetailsModel>>>
    {
        return (await axios.get(`process/${processId}/settings/statuses`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async getModuleStatusList(moduleId: string, pager: Pager): Promise<Pagination<Resource<StatusDetailsModel>>>
    {
        return (await axios.get(`modules/${moduleId}/process/settings/statuses`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async saveStatus(processId: string, form: StatusCreateModel): Promise<KeyValuePair>
    {
        return (await axios.post(`process/${processId}/settings/statuses`, form)).data;
    }

    public static async saveModuleStatus(moduleId: string, form: StatusCreateModel): Promise<Statement>
    {
        return (await axios.post(`modules/${moduleId}/process/settings/statuses`, form)).data;
    }

    public static async editStatus(processId: string, dictionaryId: number, form: StatusCreateModel): Promise<Statement>
    {
        return (await axios.post(`process/${processId}/settings/statuses/${dictionaryId}`, form)).data;
    }

    public static async editModuleStatus(moduleId: string, dictionaryId: number, form: StatusCreateModel): Promise<Statement>
    {
        return (await axios.post(`modules/${moduleId}/process/settings/statuses/${dictionaryId}`, form)).data;
    }

    public static async getStatusDetails(processId: string, id: number): Promise<StatusDetailsModel>
    {
        return (await axios.get(`process/${processId}/settings/statuses/${id}`)).data;
    }

    public static async saveSettings(processId: string, form: SettingsFormModel): Promise<ControlsResult>
    {
        return (await axios.post(`process/${processId}/settings`, form)).data;
    }

    public static async editSettings(processId: string, form: SettingsFormModel): Promise<ControlsResult>
    {
        return (await axios.put(`process/${processId}/settings`, form)).data;
    }

    public static async getFormList(processId: string, pager: Pager): Promise<Pagination<Resource<FormDetailsModel>>>
    {
        return (await axios.get(`process/${processId}/settings/forms`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async saveForm(processId: string, form: FormCreateModel): Promise<KeyValuePair>
    {
        return (await axios.post(`process/${processId}/settings/forms`, form)).data;
    }

    public static async editForm(processId: string, dictionaryId: number, form: FormCreateModel): Promise<Statement>
    {
        return (await axios.post(`process/${processId}/settings/forms/${dictionaryId}`, form)).data;
    }

    public static async getFormDetails(processId: string, id: number): Promise<FormDetailsModel>
    {
        return (await axios.get(`process/${processId}/settings/forms/${id}`)).data;
    }

    public static async saveVariables(processId: string, form: VariableCreateModel): Promise<Statement>
    {
        return (await axios.post(`process/${processId}/settings/variables`, form)).data;
    }

    public static async editVariables(processId: string, dictionaryId: number, form: VariableCreateModel): Promise<Statement>
    {
        return (await axios.post(`process/${processId}/settings/variables/${dictionaryId}`, form)).data;
    }

    public static async getVariablesDetails(processId: string, id: number): Promise<VariableDetailsModel>
    {
        return (await axios.get(`process/${processId}/settings/variables/${id}`)).data;
    }

    public static async getVariablesList(processId: string, pager: Pager): Promise<Pagination<Resource<VariableDetailsModel>>>
    {
        return (await axios.get(`process/${processId}/settings/variables`, {
            params: { ...pager.data() }
        })).data;
    }

    // Fluent

    public static async getFluentMembers(processId: string, code: string, pager: Pager, fieldType: string): Promise<Pagination<Resource<KeyValue<string, string>>>>
    {
        return (await axios.get(`simple-dictionary/process/form/${processId}/panel/fluent`, {
            params: merge({}, pager.data(), { selected: code, fieldType })
        })).data;
    }

    public static async export(publicId: string): Promise<any>
    {
        return (await axios({
            method: 'get',
            url: `process/${publicId}/export`,
            headers: { 'Accept': 'application/json' },
            responseType: 'blob'
        })).data;
    }

    public static async import(publicId: string, file: File): Promise<string[]>
    {
        const data = new FormData();

        data.append('file', file);

        return (await axios.put(`process/${publicId}/import/`, data)).data;
    }

    public static async getProcessCurrentSteps(licence: string, documentId: string): Promise<ProcessState>
    {
        return (await axios.get(`${licence}/${documentId}/current-steps`)).data;
    }

    public static async setProcessTargets(licence: string, documentId: string, form: SetTargetsModel): Promise<Statement>
    {
        return (await axios.post(`simple-command/${licence}/${documentId}/bpmn-set-targets`, form)).data;
    }

    public static async groupChangeTargets(licence: string, form: GroupChangeTargetsModel): Promise<number>
    {
        return (await axios.post(`admin/${licence}/change-targets`, form)).data;
    }

    public static async getMailerKeys(processId: string): Promise<any>
    {
        return (await axios.get(`process/flow/${processId}/notification-keys`)).data;
    }

    public static async getDocumentProcessVersions(licence: string, documentId: string, pager: Pager): Promise<Pagination<Resource<KeyValue<string, string>>>>
    {
        return (await axios.get(`admin/${licence}/${documentId}/process-module-versions`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async getProcessSteps(licence: string, documentId: string, versionId: string, pager: Pager): Promise<Pagination<Resource<KeyValue<string, string>>>>
    {
        return (await axios.get(`admin/${licence}/${documentId}/process-steps/${versionId}`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async getProcessStatuses(licence: string, documentId: string, versionId: string, pager: Pager): Promise<Pagination<Resource<KeyValue<string, string>>>>
    {
        return (await axios.get(`admin/${licence}/${documentId}/process-statuses/${versionId}`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async getDocumentProcessState(licence: string, documentId: string): Promise<Resource<ChangeProcessModel>>
    {
        return (await axios.get(`admin/${licence}/${documentId}/process-state`)).data;
    }

    public static async changeDocumentProcess(licence: string, documentId: string, form: ChangeProcessModel): Promise<Statement>
    {
        return (await axios.put(`admin/${licence}/${documentId}/change-process`, form)).data;
    }

    public static async getDocumentParticipants(licence: string, documentId: string): Promise<DocumentParticipantsModel>
    {
        return (await axios.get(`admin/${licence}/${documentId}/participants`)).data;
    }

    public static async removeDocumentParticipants(licence: string, documentId: string, form: RemoveParticipantsModel): Promise<Statement>
    {
        return (await axios.post(`admin/${licence}/${documentId}/remove-participants`, form)).data;
    }

    public static async getDocumentData(licence: string, documentId: string): Promise<Record<string, FieldInfo>>
    {
        return (await axios.get(`admin/${licence}/${documentId}/document-data`)).data;
    }

    public static async updateDocumentData(licence: string, documentId: string, form: ChangeDocumentDataModel): Promise<Statement>
    {
        return (await axios.put(`admin/${licence}/${documentId}/document-data`, form)).data;
    }

    public static async getHistoryProcessLogs(historyId: number): Promise<TaskErrorLog[]>
    {
        return (await axios.get(`process-logs/history/${historyId}`)).data;
    }

    public static async getProcessLogDetails(logId: number): Promise<TaskErrorLog>
    {
        return (await axios.get(`process-logs/${logId}`)).data;
    }

    public static async getProcessBlockLogs(processId: string, itemId: string, pager: Pager): Promise<Pagination<TaskErrorLog>>
    {
        return (await axios.get(`process-logs/process/${processId}/block/${itemId}`, {
            params: { ...pager.data() }
        })).data;
    }

    public static async getProcessLogs(processId: string, pager: Pager): Promise<Pagination<TaskErrorLog>>
    {
        return (await axios.get(`process-logs/process/${processId}`, {
            params: { ...pager.data() }
        })).data;
    }
}

export interface Version {
    name: string;
    publicId: string;
    status: string;
}

export interface CreateModel {
    title: Record<string, string>;
    description: Record<string, string>;
    date?: string;
    versions?: Version[];
    activeToUtc?: string;
}

export interface ModuleProcessModel {
    title: Record<string, string>;
    description: Record<string, string>;
    date?: string;
}

export interface ProcessFormStatement {
    description: string;
    documentType: KeyValuePair;
    publicId: string;
    status: KeyValuePair;
    title: string;
}

export interface ListItemModel {}

interface TypeModel {
    controlId: string,
    type: KeyValuePair
}

export interface DiagramDataModel {
    features: FeaturesForm[],
    types: TypeModel[]
    xml: string
}

export interface ControlsResult {
    controls: ControlsModel;
    neighbors: NeighborModel[];
}

export interface NeighborModel {
    id: string;
    step: number;
    direction: number;
}

export interface BpmnControl {
    id: string;
    type: string;
    label: string;
}
export interface FilteredBpmnControls {
    controls: BpmnControl[];
    gateway: BpmnControl[];
    startEvent: BpmnControl;
    endEvent: BpmnControl;
}

export interface ControlsModel {
    infoId: string;
    endpoint: boolean;
    condition: boolean;
    conditionResult?: boolean;
    action: boolean;
    member: boolean;
    task?: boolean;
    role: boolean;
    status: boolean;
    eventListener?: boolean;
    executeDays: boolean;
    timerEvent: boolean;
    script: boolean;
}

export interface DetailsModel {
    id: string;
    title: Record<string, string>;
    description:  Record<string, string>;
    status: KeyValuePair;
    date: string;
    documentType: KeyValuePair;
    versions: any[];
}

export interface SettingsFormModel {
    name: string;
    additionalParam: string;
    type: number | KeyValuePair;
}

export interface XmlInfoForm {
    xml: string;
    features: FeaturesForm[];
}

export interface FeaturesForm {
    id: string;
    form: ControlForm;
}

export interface ControlForm {
    actions: KeyValuePair | string;
    conditions: KeyValuePair | string;
    endpoints: KeyValuePair | string;
    roles: KeyValuePair | string;
    status: KeyValuePair | string;
    event: KeyValuePair | string;
    task: KeyValuePair;
    taskConfig: any;
    taskConfigJson: string;
}

export interface ActionCreateModel {
    originId: number;
    name: Record<string, string>;
    historyName: Record<string, string>;
    isGlobal: boolean;
    resumeAction: boolean;
    changeTargetsAction: boolean;
}

export interface ActionDetailsModel {
    originId: number;
    name: Record<string, string>;
    gridName: string;
    historyName: Record<string, string>;
    gridHistoryName: string;
    isGlobal: boolean;
}

export interface MemberCreateModel {
    originId: number;
    users: KeyValuePair[];
    name: Record<string, string>;
    isGlobal: boolean;
}

export interface MemberDetailsModel {
    originId: number;
    users: KeyValuePair[];
    name: Record<string, string>;
    gridName: string;
    isGlobal: boolean;
}

export interface RoleCreateModel {
    moduleRoleId: number;
    name: string;
}

export interface RoleDetailsModel {
    originId: number;
    moduleRoleId: number;
    name: string;
}

export interface StatusCreateModel {
    originId: number;
    name: Record<string, string>;
    statusVariantId: KeyValuePair[] | number;
    priority: number;
    graphName: Record<string, string>;
    isGlobal: boolean;
    activeToUtc: DateTime;
}

export interface StatusDetailsModel extends StatusCreateModel {
    originId: number;
    gridName: string;
    gridGraphName: string;
}

export interface FormCreateModel {
    id: number;
    originId: number;
    buttonName: Record<string, string>;
    logicFormId: KeyValuePair;
    name: Record<string, string>;
    buttonStyleId: KeyValuePair;
    dynamicFormId: KeyValuePair;
    priority: number;
    editForm: boolean;
    displayMode: KeyValuePair;
    displaySize: KeyValuePair;
    systemAction: boolean;
    signatureProvider: KeyValuePair | string;
    signatureFilesSource: number;
    toSignFields: string[] | KeyValuePair[];
    allowGroupAction: boolean;
    allowParallelGroupAction: boolean;
    multiformMode: boolean;
    subprocessId: number | KeyValuePair;
    subprocessFieldId: string | KeyValuePair;
    formButtonName: Record<string, string>;
}

export interface FormDetailsModel {
    id: number;
    originId: number;
    buttonName: Record<string, string>;
    gridButtonName: string;
    logicFormId: KeyValuePair;
    name: Record<string, string>;
    gridName: string;
    buttonStyleId: KeyValuePair;
    dynamicFormId: KeyValuePair;
    priority: number;
    successMessage: string;
    editForm: boolean;
    displayMode: KeyValuePair;
    displaySize: KeyValuePair;
    systemAction: boolean;
    signatureProvider: KeyValuePair | string;
}

export interface VariableCreateModel {
    originId: number;
    name: Record<string, string>;
    value: string;
    type: KeyValuePair;
}

export interface VariableDetailsModel extends VariableCreateModel {

}

export interface FluentResult {
    isValid: boolean;
    availableNext: KeyValuePair[];
    selected: KeyValuePair[];
}

export interface ControlTypeDetails {
    controlId: string;
    type: KeyValuePair;
}

export interface ProcessState {
    currentSteps: CurrentStep[]
}

export interface CurrentStep {
    targetId: number,
    step: KeyValuePair,
    targets: UserDisplayModel[],
    roleName: string
}

export interface SetTargetsModel {
    userTargets: number[],
    unitTargets: number[],
    teamTargets: number[],
    groupTargets: number[],
    roleName: string,
    targetId: number
}

export interface ChangeProcessModel {
    versionId: number | KeyValuePair;
    stepId: number | KeyValuePair;
    statusId: number | KeyValuePair;
}

export interface DocumentParticipantsModel
{
    userParticipants: UserDisplayModel[];
    unitParticipants: UserDisplayModel[];
    teamParticipants: UserDisplayModel[];
    groupParticipants: UserDisplayModel[];
}

export interface RemoveParticipantsModel
{
    userParticipants: string[];
    unitParticipants: string[];
    teamParticipants: string[];
    groupParticipants: string[];
}

export interface GroupChangeTargetsModel {
    documentIds: string[];
    mode: number;
    replacedUserId: string | KeyValuePair;
    replacingUserIds: string[] | KeyValuePair[];
    comment: string;
}

export interface ChangeDocumentDataModel {
    documentData: Record<string, string>;
}

export interface FieldInfo {
    name: string;
    type: string;
    data: string;
}

export interface TaskErrorLog {
    id: number;
    message: string;
    stackTrace: string;
    source: string;
    dateCreatedUtc: DateTime;
    document: TaskErrorLogDocument;
}

export interface TaskErrorLogDocument {
    title: string;
    baseUrl: string;
    publicId: string;
}
